import {
	FrequencyCadenceTypeEnum,
	rescheduleFrequenciesOptions,
	type CertificationPeriodViewModel,
	type RescheduledFrequency,
	type SearchFrequencyViewModel
} from '@generated';
import { FormModal } from '~/shared/forms/FormModal';
import Yup from '~/util/yup';
import { useGetCertificationPeriodSchedule } from '~/features/scheduling/queries';
import { useAgencyStartOfWeek } from '~/libraries/agencies/queries';
import { getSearchQueryKey } from '~/util/http/useSearch';
import { isSameCadenceCorrelator } from '../util';
import { RescheduleFrequenciesCalendar } from './RescheduleFrequenciesCalendar';
import { RescheduledFrequenciesList } from './RescheduledFrequenciesList';
import { getCadenceConstraintMessage, isRescheduled, upsertReschedule } from './util';

function buildValidationSchema(frequencies: SearchFrequencyViewModel[], agencyStartOfWeek: number) {
	return Yup.object().shape({
		frequencies: Yup.array()
			.label('Rescheduled Frequencies')
			.of(
				Yup.object().shape({
					frequencyId: Yup.string().required(),
					scheduledDateAndTime: Yup.date()
						.label('Scheduled Date and Time')
						.required()
						.test('same-cadence-correlator', function (value) {
							const original = frequencies.find(x => x.id === this.parent.frequencyId);
							if (!original || !value) {
								return true;
							}
							return (
								isSameCadenceCorrelator(original.frequencyCadenceTypeId, original.scheduledDateAndTime, value, agencyStartOfWeek) ||
								this.createError({ message: getCadenceConstraintMessage(original.frequencyCadenceTypeId) })
							);
						})
				})
			)
			.test('has-changes', 'No frequencies have been rescheduled', value =>
				(value ?? []).some(x => {
					const original = frequencies.find(y => y.id === x.frequencyId);
					return !!original && isRescheduled(original, x as RescheduledFrequency);
				})
			)
	});
}

export function RescheduleFrequenciesFormModal({
	certificationPeriod,
	frequency,
	closeModal
}: {
	certificationPeriod: CertificationPeriodViewModel;
	frequency?: SearchFrequencyViewModel;
	closeModal: () => void;
}) {
	const agencyStartOfWeek = useAgencyStartOfWeek();
	const { data: existingFrequencies } = useGetCertificationPeriodSchedule({ certificationPeriod });
	const frequencies = existingFrequencies.filter(x => x.frequencyCadenceTypeId !== FrequencyCadenceTypeEnum.Prn);

	return (
		<FormModal
			title="Reschedule Frequencies"
			closeModal={closeModal}
			initialValues={{ frequencies: [] as RescheduledFrequency[] }}
			saveMutation={{
				...rescheduleFrequenciesOptions,
				invalidateQueryKeys: [getSearchQueryKey('frequency')]
			}}
			onSuccess={closeModal}
			validationSchema={buildValidationSchema(frequencies, agencyStartOfWeek)}
			onSaveTransform={values => ({
				frequencies: (values.frequencies as RescheduledFrequency[]).filter(x =>
					isRescheduled(frequencies.find(y => y.id === x.frequencyId)!, x)
				)
			})}
			size="xl"
			modalClassName="h-[92vh]"
		>
			{formHelpers => {
				const rescheduled = formHelpers.values.frequencies as RescheduledFrequency[];
				return (
					<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
						<RescheduleFrequenciesCalendar
							certificationPeriod={certificationPeriod}
							frequencies={frequencies}
							rescheduled={rescheduled}
							highlightedFrequencyId={frequency?.id}
							agencyStartOfWeek={agencyStartOfWeek}
							onReschedule={change => formHelpers.setFieldValue('frequencies', upsertReschedule(rescheduled, change))}
						/>
						<RescheduledFrequenciesList
							frequencies={frequencies}
							rescheduled={rescheduled}
							onRemove={frequencyId =>
								formHelpers.setFieldValue(
									'frequencies',
									rescheduled.filter(x => x.frequencyId !== frequencyId)
								)
							}
						/>
					</div>
				);
			}}
		</FormModal>
	);
}
